import { elementConditionStates, formAnswerTypes } from './constants';
import { hasValue, isEmpty } from './objectUtils';
import { calculateResponseScore } from './responseUtils';

// Needed in IMMG? Set in app-config?
const dontUseConditionsFromHiddenElements = false;

export const ANY_ANSWER_CONDITION = 'ANY_ANSWER_CONDITION';

const getQuestionInCondition = (condition, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId) => {
    // If question in condition is in a different task than currentTask we get this task here
    const taskInCondition = condition.taskId === currentTask.id || !condition.taskId // No taskId = current Task
        ? currentTask
        : getProgramItem ? getProgramItem(condition.taskId) : null;

    if (taskInCondition) {
        if (dontUseConditionsFromHiddenElements) {
            const elementInCondition = taskInCondition.contentElements
                .find(element => element.questions?.find(question => question.id === condition.questionId));

            if (elementInCondition && elementInCondition.id !== currentElementId && elementInCondition.id !== parentElementId) {
                // Dont include condition if elementInCondition is hidden

                // elementInCondition.id !== currentElementId
                // - don't check state if elementInCondition is same as currentElement -> Will get recursive loop..

                // elementInCondition.id !== parentElementId
                // - don't check state if elementInCondition is same as parentElement (looped conditions - exist in ADHD program..)
                // TODO - fix this.. Rewrite show/hide logic. Use separate label-logic for accepted/expanded/rejected/...

                // eslint-disable-next-line no-use-before-define
                const conditionForElementInCondition = getElementConditionState(elementInCondition, getSpecificTaskResponses, taskInCondition, getProgramItem, currentElementId);

                if (conditionForElementInCondition === elementConditionStates.hidden) {
                    return null;
                }
            }
        }

        return taskInCondition.contentElements
            .reduce((prev, curr) => (
                prev != null ? prev : curr.questions?.find(question => question.id === condition.questionId)
            ), null);
    }
    return null;
};

const meetsConditionForQuestion = (condition, questionResponse, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId) => {
    const questionInCondition = getQuestionInCondition(condition, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId);

    // If condition is a multiple- action or single choice question with no expected answers,
    // return true if no answers has been chosen for the question in the condition
    if (condition.questionId && !condition.expectedAnswers) {
        if (!questionInCondition) return false;

        if (questionInCondition.type === formAnswerTypes.multipleChoice
            || questionInCondition.type === formAnswerTypes.singleChoice) {
            return !questionResponse?.response?.choiceResponse?.length;
        }
    }

    if (!questionResponse) {
        // If question in the condition is one of the following:
        // - A switch toggle question
        // - An action question with toggle
        // - A scale/action scale
        // and the condition is "inactive" (for toggle questions) and "min = 0" for scale questions,
        // and we don't have a response for the question we return true.
        // Toggle is false = condition is met.
        // Scale is 0 = condition is met.
        // User has to toggle question on and off for questionResponse to be set.
        // User has to explicitly set scale to 0 for questionResponse to be set.
        if (questionInCondition) {
            if (questionInCondition.type === formAnswerTypes.switch) {
                return condition?.scoreMin === 0 && condition?.scoreMax === 0;
            }

            if (questionInCondition.type === formAnswerTypes.action) {
                return condition?.scoreMin === 0 && condition?.scoreMax === 0;
            }

            if (questionInCondition.type === formAnswerTypes.scale) {
                return condition?.scoreMin === 0;
            }
        }
        return false;
    }

    if (condition.expectedAnswers != null && condition.expectedAnswers.length > 0) {
        // If question in condition is hidden questionInCondition will be null and we return false.
        if (questionInCondition) {
            if (condition.expectedAnswers.length === 1 && condition.expectedAnswers[0] === ANY_ANSWER_CONDITION) {
                return questionResponse.response?.choiceResponse?.length > 0;
            }

            return questionResponse.response?.choiceResponse?.some(c => condition.expectedAnswers.filter(d => d === c.value).length > 0) || false;
        }
        return false;
    }

    if (hasValue(condition.scoreMin) || hasValue(condition.scoreMax)) {
        const score = calculateResponseScore(questionResponse.response);
        return hasValue(score)
            && ((isEmpty(condition.scoreMin) || score >= condition.scoreMin)
                && (isEmpty(condition.scoreMax) || score <= condition.scoreMax)
            );
    }

    return true;
};

const meetsCondition = (condition, responsesForTaskInCondition, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId) => {
    if (condition.questionId) {
        const responseForQuestion = responsesForTaskInCondition && responsesForTaskInCondition[condition.questionId];
        return meetsConditionForQuestion(condition, responseForQuestion, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId);
    }

    if (condition.subtotalId) {
        const responseForSubtotal = responsesForTaskInCondition && responsesForTaskInCondition[condition.subtotalId];
        return meetsConditionForQuestion(condition, responseForSubtotal, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId);
    }

    if (condition.ifAllOthersAreHidden) {
        return condition.allOthersAreHidden;
    }

    // Condition is met if any respons on task
    return hasValue(responsesForTaskInCondition);
};

const checkConditions = (elementState, getSpecificTaskResponses, currentTask, getProgramItem, currentElementId, parentElementId) => {
    if (!elementState.conditions?.length) {
        return true; // default true if no conditions
    }

    return elementState.conditions.reduce((prev, curr) => {
        if (!prev) {
            // all conditions must be satisfied, no need to check the rest..
            return false;
        }

        // if no taskId is specified, assume current task
        const responsesForTaskInCondition = getSpecificTaskResponses(curr.taskId || currentTask.id);
        return meetsCondition(curr, responsesForTaskInCondition, currentTask, getProgramItem, getSpecificTaskResponses, currentElementId, parentElementId);
    }, true);
};

// eslint-disable-next-line arrow-body-style
export const getElementConditionState = (element, getSpecificTaskResponses, currentTask, getProgramItem, parentElementId) => {
    return element?.elementStates?.reduce((prev, curr) => {
        if (prev) { return prev; } // returns first condition that matches
        const isMet = checkConditions(curr, getSpecificTaskResponses, currentTask, getProgramItem, element.id, parentElementId);
        return isMet ? curr.state : null;
    }, null);
};

export const updateAllOthersAreHiddenProperties = (currentTask, getSpecificTaskResponses, getProgramItem) => {
    if (!getSpecificTaskResponses) {
        return;
    }
    // Get elements with an element state which has a condition with ifAllOthersAreHidden === true
    const elementsWithAllOthersAreHiddenCondition = currentTask.contentElements?.filter(
        e => e.elementStates?.some(s => s?.conditions?.some(c => c.ifAllOthersAreHidden)),
    );

    if (elementsWithAllOthersAreHiddenCondition) {
        elementsWithAllOthersAreHiddenCondition.forEach((element) => {
            // Get the elementState which contains a condition with ifAllOthersAreHidden === true
            const elementState = element.elementStates?.find(
                state => state?.conditions?.some(c => c.ifAllOthersAreHidden));

            // Get the condition with ifAllOthersAreHidden === true
            const condition = elementState?.conditions?.find(c => c.ifAllOthersAreHidden);

            // The condition could be "if all elements are hidden in another task than the current task"
            // so we get the task in the condition
            const taskInCondition = condition?.taskId === currentTask.id
                ? currentTask
                : getProgramItem ? getProgramItem(condition.taskId) : null;

            // Get all elements in the task in the condition wich do not have a ifAllOthersAreHidden condition
            const allOtherElements = taskInCondition?.contentElements?.filter(
                e => !e.elementStates?.some(s => s?.conditions?.some(c => c.ifAllOthersAreHidden)),
            );

            if (allOtherElements
                .every(e => getElementConditionState(e, getSpecificTaskResponses, currentTask, getProgramItem) === elementConditionStates.hidden)) {
                // All other elements are currently hidden
                condition.allOthersAreHidden = true;
            } else {
                // All other elements are currently not hidden
                condition.allOthersAreHidden = false;
            }
        });
    }
};
